import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { TEnkIWPMelding, TEnkIWPNieuweMelding, TEnkIWPMeldingSoortItem, TEnkIWPServiceContractItem, TEnkIWPInstallatieItem, EnkIWPServerMeldingen as ApiMeldingen, EnkIWPServerPhotos as ApiPhotos } from "../api/api-iwp-server"
import EnkPageContainer from "../components/page-container"
import EnkContentLoadingAndError from "../components/error-box"
import { useAuth } from "../auth/use-auth"
import { ChevronUpIcon, ChevronDownIcon, PhotoIcon, PlusIcon, XMarkIcon, ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline"

type SortField = "meldingnr" | "aanmaakDatum" | "mutatieDatum" | "omschrijving" | "meldingDoor" | "recordstatus" | "meldingsoort" | "prioriteit"
type SortDir = "asc" | "desc"

const statusStyles: Record<number, { class: string; text: string }> = {
  10: { class: "text-blue-700 bg-blue-50 ring-blue-600/20", text: "Nieuw" },
  20: { class: "text-yellow-700 bg-yellow-50 ring-yellow-600/20", text: "Onderhanden" },
  30: { class: "text-green-700 bg-green-50 ring-green-600/20", text: "Afgesloten" },
}

const prioriteitStyles: Record<number, { class: string; text: string }> = {
  1: { class: "text-red-700 bg-red-50 ring-red-600/20", text: "Hoog" },
  2: { class: "text-gray-700 bg-gray-50 ring-gray-500/10", text: "Normaal" },
  3: { class: "text-blue-600 bg-blue-50 ring-blue-500/10", text: "Laag" },
}

const statusTabs = [
  { label: "Alle", value: 0 },
  { label: "Nieuw", value: 10 },
  { label: "Onderhanden", value: 20 },
  { label: "Afgesloten", value: 30 },
]

function StatusBadge({ status }: { status: number }) {
  const style = statusStyles[status] || { class: "text-gray-600 bg-gray-50 ring-gray-500/10", text: status.toString() }
  return (
    <span className={`${style.class} inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset`}>
      {style.text}
    </span>
  )
}

function PrioriteitBadge({ prioriteit }: { prioriteit: number }) {
  if (!prioriteit) return null
  const style = prioriteitStyles[prioriteit] || { class: "text-gray-600 bg-gray-50 ring-gray-500/10", text: prioriteit.toString() }
  return (
    <span className={`${style.class} inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset`}>
      {style.text}
    </span>
  )
}

const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png"]

// Resize + comprimeer een foto in de browser tot een JPEG base64-string, op dezelfde
// manier als de profielfoto-upload (canvas + toDataURL). Een telefoonfoto van enkele MB's
// wordt zo teruggebracht tot een paar honderd KB, zodat de upload betrouwbaar door de
// API-keten komt. De afbeelding wordt naar max csFotoMaxDimensie px geschaald (langste
// zijde) met behoud van aspect ratio. Retourneert de base64 zonder data-URL prefix.
const csFotoMaxDimensie = 1600
const csFotoJpegKwaliteit = 0.85

function resizeImageToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error("Kon de foto niet lezen"))
    reader.onload = (e) => {
      const img = new Image()
      img.onerror = () => reject(new Error("Kon de foto niet laden"))
      img.onload = () => {
        let width = img.width
        let height = img.height
        if (width > height) {
          if (width > csFotoMaxDimensie) {
            height = Math.round((height * csFotoMaxDimensie) / width)
            width = csFotoMaxDimensie
          }
        } else {
          if (height > csFotoMaxDimensie) {
            width = Math.round((width * csFotoMaxDimensie) / height)
            height = csFotoMaxDimensie
          }
        }
        const canvas = document.createElement("canvas")
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext("2d")
        ctx?.drawImage(img, 0, 0, width, height)
        const dataUrl = canvas.toDataURL("image/jpeg", csFotoJpegKwaliteit)
        resolve(dataUrl.split(",")[1]) // verwijder de "data:image/jpeg;base64," prefix
      }
      img.src = e.target?.result as string
    }
    reader.readAsDataURL(file)
  })
}

function formatDateFull(dateStr: string) {
  if (!dateStr) return "-"
  const date = new Date(dateStr)
  return date.toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
}

// --- Nieuw melding formulier ---

function NieuweMeldingForm({ soorten, contracten, onSubmit, onCancel, submitting, onLoadInstallaties }: {
  soorten: TEnkIWPMeldingSoortItem[]
  contracten: TEnkIWPServiceContractItem[]
  onSubmit: (melding: TEnkIWPNieuweMelding, fotos: File[]) => void
  onCancel: () => void
  submitting: boolean
  onLoadInstallaties: (contractnr: string) => Promise<TEnkIWPInstallatieItem[]>
}) {
  const [omschrijving, setOmschrijving] = useState("")
  const [melding, setMelding] = useState("")
  const [meldingsoort, setMeldingsoort] = useState("")
  const [contractnr, setContractnr] = useState("")
  const [installaties, setInstallaties] = useState<TEnkIWPInstallatieItem[]>([])
  const [installatienr, setInstallatienr] = useState("")
  const prioriteit = 0
  const [fotos, setFotos] = useState<File[]>([])
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const loadInstallatiesRef = useRef(onLoadInstallaties)
  loadInstallatiesRef.current = onLoadInstallaties

  useEffect(() => {
    setInstallatienr("")
    loadInstallatiesRef.current(contractnr).then(setInstallaties).catch(() => setInstallaties([]))
  }, [contractnr])

  const addFiles = useCallback((files: FileList | File[]) => {
    const valid = Array.from(files).filter((f) => ALLOWED_PHOTO_TYPES.includes(f.type))
    if (valid.length > 0) setFotos((prev) => [...prev, ...valid])
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    addFiles(e.dataTransfer.files)
  }, [addFiles])

  const removeFoto = (index: number) => {
    setFotos((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({ omschrijving, melding, meldingsoort, prioriteit, contractnr, installatienr }, fotos)
  }

  return (
    <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">Nieuwe melding</h3>
        <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-500">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="omschrijving" className="block text-sm font-medium text-gray-700">
            Omschrijving
          </label>
          <input
            type="text"
            id="omschrijving"
            maxLength={100}
            required
            value={omschrijving}
            onChange={(e) => setOmschrijving(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            placeholder="Korte omschrijving van de melding"
          />
        </div>
        <div>
          <label htmlFor="melding" className="block text-sm font-medium text-gray-700">
            Beschrijving
          </label>
          <textarea
            id="melding"
            rows={4}
            required
            value={melding}
            onChange={(e) => setMelding(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            placeholder="Uitgebreide beschrijving van de melding"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {soorten.length > 0 && (
            <div>
              <label htmlFor="meldingsoort" className="block text-sm font-medium text-gray-700">
                Meldingsoort
              </label>
              <select
                id="meldingsoort"
                value={meldingsoort}
                onChange={(e) => setMeldingsoort(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              >
                <option value="">-- Kies een soort --</option>
                {soorten.map((s) => (
                  <option key={s.code} value={s.code}>{s.omschrijving || s.code}</option>
                ))}
              </select>
            </div>
          )}

          {contracten.length > 0 && (
            <div>
              <label htmlFor="contractnr" className="block text-sm font-medium text-gray-700">
                Servicecontract
              </label>
              <select
                id="contractnr"
                value={contractnr}
                onChange={(e) => setContractnr(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              >
                <option value="">-- Geen contract --</option>
                {contracten.map((c) => (
                  <option key={c.contractnr} value={c.contractnr}>{c.contractnr} - {c.omschrijving}</option>
                ))}
              </select>
            </div>
          )}

          {installaties.length > 0 && (
            <div>
              <label htmlFor="installatienr" className="block text-sm font-medium text-gray-700">
                Installatie
              </label>
              <select
                id="installatienr"
                value={installatienr}
                onChange={(e) => setInstallatienr(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
              >
                <option value="">-- Kies een installatie --</option>
                {installaties.map((inst) => (
                  <option key={inst.installatienr} value={inst.installatienr}>{inst.installatienr} - {inst.omschrijving}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Foto upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Foto's</label>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
              dragging ? "border-primary bg-primary/5" : "border-gray-300 hover:border-gray-400"
            }`}
          >
            <PhotoIcon className="mx-auto h-10 w-10 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">Sleep foto's hierheen of klik om te kiezen</p>
            <p className="mt-1 text-xs text-gray-400">JPG, PNG, GIF, BMP, WebP</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALLOWED_PHOTO_TYPES.join(",")}
              className="hidden"
              onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = "" }}
            />
          </div>

          {fotos.length > 0 && (
            <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {fotos.map((foto, idx) => (
                <li key={idx} className="relative group">
                  <img
                    src={URL.createObjectURL(foto)}
                    alt={foto.name}
                    className="h-24 w-full rounded-md object-cover ring-1 ring-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => removeFoto(idx)}
                    className="absolute -top-2 -right-2 rounded-full bg-red-500 p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                  <p className="mt-1 truncate text-xs text-gray-500">{foto.name}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            Annuleren
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting ? "Versturen..." : "Melding versturen"}
          </button>
        </div>
      </form>
    </div>
  )
}

// --- Hoofdpagina ---

export default function EnkMeldingen() {
  const [meldingen, setMeldingen] = useState<TEnkIWPMelding[]>([])
  const [soorten, setSoorten] = useState<TEnkIWPMeldingSoortItem[]>([])
  const [contracten, setContracten] = useState<TEnkIWPServiceContractItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [sortField, setSortField] = useState<SortField>("aanmaakDatum")
  const [sortDir, setSortDir] = useState<SortDir>("desc")
  const [filterText, setFilterText] = useState("")
  const [statusFilter, setStatusFilter] = useState(0)
  const { getCurrentGroupId } = useAuth()
  const navigate = useNavigate()

  const fetchMeldingen = useCallback(() => {
    setLoading(true)
    ApiMeldingen.getList(getCurrentGroupId(), statusFilter)
      .then((data) => {
        setMeldingen(data)
        setError(null)
        setLoading(false)
      })
      .catch((err) => {
        console.error("Failed to fetch meldingen:", err)
        setError("Er is een fout opgetreden bij het ophalen van de meldingen.")
        setLoading(false)
      })
  }, [getCurrentGroupId, statusFilter])

  useEffect(() => {
    fetchMeldingen()
  }, [fetchMeldingen])

  useEffect(() => {
    ApiMeldingen.getSoorten()
      .then(setSoorten)
      .catch(() => { /* soorten zijn optioneel */ })
    ApiMeldingen.getContracten(getCurrentGroupId())
      .then(setContracten)
      .catch(() => { /* contracten zijn optioneel */ })
  }, [getCurrentGroupId])

  const loadInstallaties = useCallback((contractnr: string) => {
    return ApiMeldingen.getInstallaties(getCurrentGroupId(), contractnr)
  }, [getCurrentGroupId])

  const handleCreate = async (melding: TEnkIWPNieuweMelding, fotos: File[]) => {
    setSubmitting(true)
    try {
      const result = await ApiMeldingen.createMelding(getCurrentGroupId(), melding)

      for (const foto of fotos) {
        // Resize + comprimeer naar JPEG base64 (zoals de profielfoto-upload), zodat de
        // payload klein blijft en betrouwbaar door de API-keten komt.
        const base64 = await resizeImageToBase64(foto)
        await ApiPhotos.uploadPhoto("MeldingenBijlage", result.meldingnr.toString(), foto.name, ".jpg", base64)
      }

      setShowForm(false)
      fetchMeldingen()
    } catch (err) {
      console.error("Failed to create melding:", err)
      setError("Er is een fout opgetreden bij het aanmaken van de melding.")
    } finally {
      setSubmitting(false)
    }
  }

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortField(field)
      setSortDir("asc")
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null
    return sortDir === "asc"
      ? <ChevronUpIcon className="inline h-4 w-4 ml-1" />
      : <ChevronDownIcon className="inline h-4 w-4 ml-1" />
  }

  const gewijzigdCount = meldingen.filter((m) => m.isGewijzigd).length

  const sorted = [...meldingen]
    .filter((m) => {
      if (!filterText) return true
      const q = filterText.toLowerCase()
      return (
        m.omschrijving?.toLowerCase().includes(q) ||
        m.meldingDoor?.toLowerCase().includes(q) ||
        m.meldingsoort?.toLowerCase().includes(q) ||
        m.prioriteitLabel?.toLowerCase().includes(q) ||
        m.meldingnr.toString().includes(q) ||
        m.statusLabel?.toLowerCase().includes(q)
      )
    })
    .sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1
      const va = a[sortField] ?? ""
      const vb = b[sortField] ?? ""
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir
      return String(va).localeCompare(String(vb)) * dir
    })

  return (
    <EnkPageContainer>
      {/* Status tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex gap-x-6" aria-label="Tabs">
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setStatusFilter(tab.value)}
              className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
                statusFilter === tab.value
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Zoeken..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          className="rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm w-48"
        />
        <div className="flex items-center gap-3">
          {gewijzigdCount > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              {gewijzigdCount} gewijzigd
            </span>
          )}
          {!showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-x-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90"
            >
              <PlusIcon className="-ml-0.5 h-5 w-5" />
              Nieuwe melding
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <NieuweMeldingForm soorten={soorten} contracten={contracten} onSubmit={(melding, fotos) => handleCreate(melding, fotos)} onCancel={() => setShowForm(false)} submitting={submitting} onLoadInstallaties={loadInstallaties} />
      )}

      <EnkContentLoadingAndError loading={loading} error={error}>
        {sorted.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm text-gray-500">{filterText ? "Geen meldingen gevonden voor deze zoekopdracht" : "Geen meldingen gevonden"}</p>
          </div>
        ) : (
          <div className="overflow-x-auto bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300 table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="w-6 py-3.5 pl-3 pr-0 sm:pl-4"></th>
                  <th scope="col" className="w-12 py-3.5 pr-2 text-left text-sm font-semibold text-gray-900 cursor-pointer select-none" onClick={() => toggleSort("meldingnr")}>Nr<SortIcon field="meldingnr" /></th>
                  <th scope="col" className="w-36 px-2 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer select-none" onClick={() => toggleSort("aanmaakDatum")}>Aangemaakt<SortIcon field="aanmaakDatum" /></th>
                  <th scope="col" className="w-36 px-2 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer select-none" onClick={() => toggleSort("mutatieDatum")}>Laatst gewijzigd<SortIcon field="mutatieDatum" /></th>
                  <th scope="col" className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer select-none" onClick={() => toggleSort("omschrijving")}>Omschrijving<SortIcon field="omschrijving" /></th>
                  <th scope="col" className="w-32 px-2 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer select-none" onClick={() => toggleSort("meldingDoor")}>Door<SortIcon field="meldingDoor" /></th>
                  <th scope="col" className="w-28 px-2 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer select-none" onClick={() => toggleSort("meldingsoort")}>Soort<SortIcon field="meldingsoort" /></th>
                  <th scope="col" className="w-24 px-2 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer select-none" onClick={() => toggleSort("prioriteit")}>Prioriteit<SortIcon field="prioriteit" /></th>
                  <th scope="col" className="w-28 px-2 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer select-none" onClick={() => toggleSort("recordstatus")}>Status<SortIcon field="recordstatus" /></th>
                  <th scope="col" className="w-8 px-2 py-3.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {sorted.map((m) => (
                  <tr
                    key={m.meldingnr}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/meldingen/${m.meldingnr}`)}
                  >
                    <td className="py-3 pl-3 pr-0 sm:pl-4">
                      {m.isGewijzigd && (
                        <span className="relative flex h-2 w-2" title="Gewijzigd in ENK">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap py-3 pr-2 text-sm font-medium text-gray-900">{m.meldingnr}</td>
                    <td className="whitespace-nowrap px-2 py-3 text-sm text-gray-500">{formatDateFull(m.aanmaakDatum)}</td>
                    <td className="whitespace-nowrap px-2 py-3 text-sm text-gray-500">{formatDateFull(m.mutatieDatum)}</td>
                    <td className="px-2 py-3 text-sm text-gray-900 truncate max-w-xs">{m.omschrijving}</td>
                    <td className="whitespace-nowrap px-2 py-3 text-sm text-gray-500 truncate">{m.meldingDoor}</td>
                    <td className="whitespace-nowrap px-2 py-3 text-sm text-gray-500">{m.meldingsoortLabel || m.meldingsoort || "-"}</td>
                    <td className="whitespace-nowrap px-2 py-3 text-sm"><PrioriteitBadge prioriteit={m.prioriteit} /></td>
                    <td className="whitespace-nowrap px-2 py-3 text-sm"><StatusBadge status={m.recordstatus} /></td>
                    <td className="whitespace-nowrap px-2 py-3 text-sm text-center">
                      {m.heeftTerugkoppeling && (
                        <ChatBubbleLeftRightIcon className="inline h-4 w-4 text-primary" title="Heeft terugkoppeling" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </EnkContentLoadingAndError>

    </EnkPageContainer>
  )
}
